import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { TextField, Box, FormControl, InputLabel, Select, MenuItem, Button, CircularProgress, Link as MuiLink } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';

// Custom centered loading overlay component
const CenteredLoadingOverlay = () => (
    <Box
        sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 1,
        }}
    >
        <CircularProgress size={60} />
    </Box>
);

const PitcherAttributesTable = ({ apiUrl }) => {
    const [players, setPlayers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [downloading, setDownloading] = useState(false);

    // Pagination state
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 50,
    });
    const [rowCount, setRowCount] = useState(0);

    // State for filters
    const [selectedTeam, setSelectedTeam] = useState('');
    const [selectedPosition, setSelectedPosition] = useState('');
    const [selectedSeason, setSelectedSeason] = useState('');
    const [selectedArchetype, setSelectedArchetype] = useState('');

    // State for dropdown options
    const [teamOptions, setTeamOptions] = useState([]);
    const [positionOptions, setPositionOptions] = useState([]);
    const [seasonOptions, setSeasonOptions] = useState([]);
    const [archetypeOptions, setArchetypeOptions] = useState([]);

    // Ref to track if initial load is complete
    const initialLoadComplete = useRef(false);
    const filterOptionsLoaded = useRef(false);

    // Build query parameters for API call
    const buildQueryParams = useCallback(() => {
        const params = new URLSearchParams();

        // Pagination
        params.append('page', paginationModel.page + 1);
        params.append('page_size', paginationModel.pageSize);

        // Search
        if (searchTerm) {
            params.append('search', searchTerm);
        }

        // Filters
        if (selectedTeam) params.append('team', selectedTeam);
        if (selectedPosition) params.append('position', selectedPosition);
        if (selectedSeason) params.append('season', selectedSeason);
        if (selectedArchetype) params.append('archetype', selectedArchetype);

        return params;
    }, [paginationModel, searchTerm, selectedTeam, selectedPosition, selectedSeason, selectedArchetype]);

    // Fetch filter options once on mount
    const fetchFilterOptions = useCallback(async () => {
        if (filterOptionsLoaded.current) return;

        try {
            // Remove /paginated from the URL for filter-options endpoint
            const filterOptionsUrl = apiUrl.replace('/paginated', '/filter-options');
            const response = await fetch(filterOptionsUrl);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const filterOptions = await response.json();
            
            if (filterOptions.teams) {
                setTeamOptions(filterOptions.teams);
            }
            
            if (filterOptions.positions) {
                setPositionOptions(filterOptions.positions);
            }
            
            if (filterOptions.seasons) {
                setSeasonOptions(filterOptions.seasons);
            }
            
            if (filterOptions.archetypes) {
                setArchetypeOptions(filterOptions.archetypes);
            }

            filterOptionsLoaded.current = true;
        } catch (error) {
            console.error("Error fetching filter options:", error);
        }
    }, [apiUrl]);

    // Fetch data with server-side pagination
    const fetchData = useCallback(async () => {
        setLoading(true);
        setPlayers([]); // Clear data immediately to show loading state

        try {
            const params = buildQueryParams();
            const url = `${apiUrl}?${params.toString()}`;

            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const responseData = await response.json();

            // Handle paginated response
            const data = responseData.data || responseData;
            const paginationInfo = responseData.pagination || {};

            const playersWithIds = data.map((player, index) => {
                // Build pitches string
                const pitches = [];
                for (let i = 1; i <= 5; i++) {
                    const pitchName = player.pitching_attributes?.[`pitch_${i}_name`];
                    const pitchValue = player.pitching_attributes?.[`pitch_${i}_value`];
                    if (pitchName && pitchValue) {
                        pitches.push(`${pitchName}: ${pitchValue}`);
                    }
                }

                return {
                    ...player,
                    id: player.uid || index,
                    usernameDisplay: player.user ? player.user.username : 'N/A',
                    user_url: player.user?.url || null,
                    team_name: player.team ? player.team.team_name : 'N/A',
                    // Flatten pitching attributes for easier access
                    pitching_archetype: player.pitching_attributes?.pitching_archetype || 'N/A',
                    velocity: player.pitching_attributes?.velocity,
                    movement_vs_lhb: player.pitching_attributes?.movement_vs_lhb,
                    movement_vs_rhb: player.pitching_attributes?.movement_vs_rhb,
                    control_vs_lhb: player.pitching_attributes?.control_vs_lhb,
                    control_vs_rhb: player.pitching_attributes?.control_vs_rhb,
                    p_babip: player.pitching_attributes?.p_babip,
                    stamina: player.pitching_attributes?.stamina,
                    holding_runners: player.pitching_attributes?.holding_runners,
                    gb_percentage: player.pitching_attributes?.gb_percentage,
                    arm_slot: player.pitching_attributes?.arm_slot,
                    // Individual pitch fields
                    pitch_1_name: player.pitching_attributes?.pitch_1_name,
                    pitch_1_value: player.pitching_attributes?.pitch_1_value,
                    pitch_2_name: player.pitching_attributes?.pitch_2_name,
                    pitch_2_value: player.pitching_attributes?.pitch_2_value,
                    pitch_3_name: player.pitching_attributes?.pitch_3_name,
                    pitch_3_value: player.pitching_attributes?.pitch_3_value,
                    pitch_4_name: player.pitching_attributes?.pitch_4_name,
                    pitch_4_value: player.pitching_attributes?.pitch_4_value,
                    pitch_5_name: player.pitching_attributes?.pitch_5_name,
                    pitch_5_value: player.pitching_attributes?.pitch_5_value,
                    total_pitch_changes: player.pitching_attributes?.total_pitch_changes,
                    total_arm_slot_changes: player.pitching_attributes?.total_arm_slot_changes,
                };
            });

            setPlayers(playersWithIds);
            setRowCount(paginationInfo.total_count || data.length);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching data:", error);
            setError(error);
            setLoading(false);
        }
    }, [apiUrl, buildQueryParams]);

    // Load filter options once on mount
    useEffect(() => {
        fetchFilterOptions();
    }, [fetchFilterOptions]);

    // Initial load and refetch when dependencies change with debouncing
    useEffect(() => {
        if (!initialLoadComplete.current) {
            fetchData();
            initialLoadComplete.current = true;
            return;
        }

        // Debounce for search and filter changes (500ms)
        const timeoutId = setTimeout(() => {
            fetchData();
        }, 500);

        return () => clearTimeout(timeoutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel, searchTerm, selectedTeam, selectedPosition, selectedSeason, selectedArchetype]);

    const createDropdown = (label, value, setValue, options) => (
        <FormControl sx={{ minWidth: 150, marginRight: 2 }}>
            <InputLabel>{label}</InputLabel>
            <Select
                value={value}
                label={label}
                onChange={(e) => setValue(e.target.value)}
            >
                <MenuItem value="">All</MenuItem>
                {options.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
            </Select>
        </FormControl>
    );

    const downloadCSV = async () => {
        setDownloading(true);

        try {
            // Calculate total pages needed
            const totalPages = Math.ceil(rowCount / 100); // Use max page size of 100
            const allPlayers = [];

            // Fetch all pages
            for (let page = 1; page <= totalPages; page++) {
                const params = new URLSearchParams();
                params.append('page', page.toString());
                params.append('page_size', '100');

                // Apply current filters
                if (searchTerm) params.append('search', searchTerm);
                if (selectedTeam) params.append('team', selectedTeam);
                if (selectedPosition) params.append('position', selectedPosition);
                if (selectedSeason) params.append('season', selectedSeason);
                if (selectedArchetype) params.append('archetype', selectedArchetype);

                const response = await fetch(`${apiUrl}?${params.toString()}`);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const responseData = await response.json();
                const data = responseData.data || responseData;

                allPlayers.push(...data.map(player => {
                    return {
                        ...player,
                        usernameDisplay: player.user ? player.user.username : 'N/A',
                        user_url: player.user?.url || null,
                        team_name: player.team ? player.team.team_name : 'N/A',
                        pitching_archetype: player.pitching_attributes?.pitching_archetype || 'N/A',
                        velocity: player.pitching_attributes?.velocity,
                        movement_vs_lhb: player.pitching_attributes?.movement_vs_lhb,
                        movement_vs_rhb: player.pitching_attributes?.movement_vs_rhb,
                        control_vs_lhb: player.pitching_attributes?.control_vs_lhb,
                        control_vs_rhb: player.pitching_attributes?.control_vs_rhb,
                        p_babip: player.pitching_attributes?.p_babip,
                        stamina: player.pitching_attributes?.stamina,
                        holding_runners: player.pitching_attributes?.holding_runners,
                        gb_percentage: player.pitching_attributes?.gb_percentage,
                        arm_slot: player.pitching_attributes?.arm_slot,
                        pitch_1_name: player.pitching_attributes?.pitch_1_name,
                        pitch_1_value: player.pitching_attributes?.pitch_1_value,
                        pitch_2_name: player.pitching_attributes?.pitch_2_name,
                        pitch_2_value: player.pitching_attributes?.pitch_2_value,
                        pitch_3_name: player.pitching_attributes?.pitch_3_name,
                        pitch_3_value: player.pitching_attributes?.pitch_3_value,
                        pitch_4_name: player.pitching_attributes?.pitch_4_name,
                        pitch_4_value: player.pitching_attributes?.pitch_4_value,
                        pitch_5_name: player.pitching_attributes?.pitch_5_name,
                        pitch_5_value: player.pitching_attributes?.pitch_5_value,
                        total_pitch_changes: player.pitching_attributes?.total_pitch_changes,
                        total_arm_slot_changes: player.pitching_attributes?.total_arm_slot_changes,
                    };
                }));
            }

            // Generate CSV
            const headers = [
                'Player Name', 'Username', 'Number', 'Pos', 'Team', 'Throws',
                'TPE', 'TPE Banked', 'Season', 'Archetype',
                'Velocity', 'Stamina', 'Arm Slot', 'GB%', 'Hold Runners', 'P BABIP',
                'Movement vs LHB', 'Control vs LHB',
                'Movement vs RHB', 'Control vs RHB',
                'Pitch 1', 'Pitch 1 Value', 'Pitch 2', 'Pitch 2 Value', 'Pitch 3', 'Pitch 3 Value',
                'Pitch 4', 'Pitch 4 Value', 'Pitch 5', 'Pitch 5 Value',
                'Pitch Changes', 'Arm Slot Changes'
            ];

            const csvData = allPlayers.map(player => [
                player.player_name,
                player.usernameDisplay,
                player.number,
                player.position,
                player.team_name,
                player.throws,
                player.tpe,
                player.tpe_banked,
                player.season,
                player.pitching_archetype,
                player.velocity,
                player.stamina,
                player.arm_slot,
                player.gb_percentage,
                player.holding_runners,
                player.p_babip,
                player.movement_vs_lhb,
                player.control_vs_lhb,
                player.movement_vs_rhb,
                player.control_vs_rhb,
                player.pitch_1_name,
                player.pitch_1_value,
                player.pitch_2_name,
                player.pitch_2_value,
                player.pitch_3_name,
                player.pitch_3_value,
                player.pitch_4_name,
                player.pitch_4_value,
                player.pitch_5_name,
                player.pitch_5_value,
                player.total_pitch_changes,
                player.total_arm_slot_changes
            ].map(cell => {
                const cellStr = cell?.toString() || '';
                if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
                    return `"${cellStr.replace(/"/g, '""')}"`;
                }
                return cellStr;
            }).join(','));

            const csvContent = [headers.join(','), ...csvData].join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `pitcher_attributes_${new Date().toISOString().split('T')[0]}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error downloading CSV:', error);
            alert('Error downloading CSV. Please try again.');
        } finally {
            setDownloading(false);
        }
    };

    const columns = [
        {
            field: 'player_name',
            headerName: 'Player Name',
            width: 180,
            frozen: true,
            renderCell: (params) => (
                params.row.url ? (
                    <MuiLink
                        href={params.row.url}
                        target="_blank"
                        rel="noopener"
                        sx={{
                            color: 'primary.main',
                            textDecoration: 'none',
                            '&:hover': {
                                textDecoration: 'underline',
                            },
                        }}
                    >
                        {params.value}
                    </MuiLink>
                ) : params.value
            ),
        },
        {
            field: 'usernameDisplay',
            headerName: 'Username',
            width: 130,
            renderCell: (params) => (
                params.row.user_url ? (
                    <MuiLink
                        href={params.row.user_url}
                        target="_blank"
                        rel="noopener"
                        sx={{
                            color: 'primary.main',
                            textDecoration: 'none',
                            '&:hover': {
                                textDecoration: 'underline',
                            },
                        }}
                    >
                        {params.value}
                    </MuiLink>
                ) : params.value
            ),
        },
        { field: 'number', headerName: 'Number', width: 80, type: 'number' },
        { field: 'position', headerName: 'Pos', width: 70 },
        { field: 'team_name', headerName: 'Team', width: 180 },
        { field: 'throws', headerName: 'Throws', width: 90 },
        { field: 'tpe', headerName: 'TPE', width: 90, type: 'number' },
        { field: 'tpe_banked', headerName: 'TPE Banked', width: 110, type: 'number' },
        { field: 'season', headerName: 'Season', width: 90, type: 'number' },
        { field: 'pitching_archetype', headerName: 'Archetype', width: 240 },

        // Core attributes
        { field: 'velocity', headerName: 'Velocity', width: 110 },
        { field: 'stamina', headerName: 'Stamina', width: 100, type: 'number' },
        { field: 'arm_slot', headerName: 'Arm Slot', width: 140 },
        { field: 'gb_percentage', headerName: 'GB%', width: 90, type: 'number' },
        { field: 'holding_runners', headerName: 'Hold Runners', width: 130, type: 'number' },
        { field: 'p_babip', headerName: 'P BABIP', width: 100, type: 'number' },

        // vs LHB
        { field: 'movement_vs_lhb', headerName: 'Movement vs LHB', width: 150, type: 'number' },
        { field: 'control_vs_lhb', headerName: 'Control vs LHB', width: 140, type: 'number' },

        // vs RHB
        { field: 'movement_vs_rhb', headerName: 'Movement vs RHB', width: 150, type: 'number' },
        { field: 'control_vs_rhb', headerName: 'Control vs RHB', width: 140, type: 'number' },
        
        // Pitches
        { field: 'pitch_1_name', headerName: 'Pitch 1', width: 130 },
        { field: 'pitch_1_value', headerName: 'Pitch 1 Value', width: 120, type: 'number' },
        { field: 'pitch_2_name', headerName: 'Pitch 2', width: 130 },
        { field: 'pitch_2_value', headerName: 'Pitch 2 Value', width: 120, type: 'number' },
        { field: 'pitch_3_name', headerName: 'Pitch 3', width: 130 },
        { field: 'pitch_3_value', headerName: 'Pitch 3 Value', width: 120, type: 'number' },
        { field: 'pitch_4_name', headerName: 'Pitch 4', width: 130 },
        { field: 'pitch_4_value', headerName: 'Pitch 4 Value', width: 120, type: 'number' },
        { field: 'pitch_5_name', headerName: 'Pitch 5', width: 130 },
        { field: 'pitch_5_value', headerName: 'Pitch 5 Value', width: 120, type: 'number' },
        
        { field: 'total_pitch_changes', headerName: 'Pitch Changes', width: 130, type: 'number' },
        { field: 'total_arm_slot_changes', headerName: 'Arm Slot Changes', width: 150, type: 'number' }
    ];

    if (error) return <div>Error loading data: {error.message}</div>;

    return (
        <Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, marginBottom: 2, alignItems: 'center' }}>
                <TextField
                    label="Search by Player or Username"
                    variant="outlined"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ minWidth: 250 }}
                />
                {createDropdown("Team", selectedTeam, setSelectedTeam, teamOptions)}
                {createDropdown("Position", selectedPosition, setSelectedPosition, positionOptions)}
                {createDropdown("Season", selectedSeason, setSelectedSeason, seasonOptions)}
                {createDropdown("Archetype", selectedArchetype, setSelectedArchetype, archetypeOptions)}
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={downloading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                    onClick={downloadCSV}
                    disabled={downloading}
                    sx={{ height: 56 }}
                >
                    {downloading ? 'Downloading...' : 'Download CSV'}
                </Button>
            </Box>
            <div style={{ height: 600, width: '100%', position: 'relative' }}>
                <DataGrid
                    rows={players}
                    columns={columns}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={[25, 50, 100]}
                    rowCount={rowCount}
                    paginationMode="server"
                    loading={loading}
                    disableSelectionOnClick
                    slots={{
                        loadingOverlay: CenteredLoadingOverlay,
                    }}
                />
            </div>
        </Box>
    );
};

export default PitcherAttributesTable;
